import { useCallback, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { FilterState } from '@/features/filterSlice';
import { useFetchNetworkGroupsQuery } from '@/features/networkGroupSlice';
import { initialState } from '@/features/filterSlice';
import { isFilterEmpty } from '@/features/filterHelpers';
import {
  useFetchFieldSpeciesQuery,
  useFetchFieldsQuery,
  useVoltageInKv,
  useFetchWorkStatusQuery,
  type Species,
} from '@/features/fieldSlice';
import { CLEAR_TAG, useClientTerminology } from '@/views/list/NetworkTerms';
import { useShowClearSpanAnalysis } from '@/hooks/useShowClearSpanAnalysis';
import TagManager from 'react-gtm-module';
import {
  PriorityRatingType,
  WildfireRiskRating,
  Rating,
  NetworkType,
} from '@/features/types';
import type { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import type { TagField } from '@/features/types';
import {
  GoogleEvents,
  getGoogleEventForAnalysis,
} from '@/features/googleEvents';
import { formatToEventString } from '@/helpers/stringConversion';
import { getGroupNameFromId } from '@/helpers/networkGroups';
import { ALL_NETWORKS } from '@/app/constants';
import { groupIdsInFilter, getRatingOptions } from '@/views/filter/utils';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import { useAppSelector } from '@/app/hooks';

interface MappingData {
  species?: Species[];
  tags?: TagField[];
}

// Lookup the enum value by its numeric key
type EnumValueLookup = Record<number, string>;

const sendGoogleEvents = (
  data: unknown,
  event: string,
  type?: keyof MappingData | EnumValueLookup,
  mappingData?: MappingData
) => {
  const getData = (
    items: { id: number; name: string }[] = [],
    item: string
  ) => {
    const findItem = items.find((s) => s.id === Number(item));
    if (!findItem) return '';
    return formatToEventString(findItem.name);
  };

  if (Array.isArray(data) && data.length > 0) {
    data.map((s) =>
      TagManager.dataLayer({
        dataLayer: {
          event: type
            ? `${event}_${
                type === 'species' || type === 'tags'
                  ? getData(mappingData?.[type as keyof MappingData], s)
                  : formatToEventString(type[s])
              }`
            : `${event}_${formatToEventString(s)}`,
        },
      })
    );
    return;
  }
  TagManager.dataLayer({
    dataLayer: {
      event: formatToEventString(event),
    },
  });
};

const sendGoogleEventsForRedFlagWarningFilter = (
  formData: string[],
  redFlagWarnings: RedFlagWarning[]
) => {
  TagManager.dataLayer({
    dataLayer: {
      event: GoogleEvents.redFlagWarningFilter,
      redFlagWarningFilters: redFlagWarnings
        .filter((x) => formData.includes(x.id))
        .map((x) => x.description),
    },
  });
};

const GoogleEventsForFilter = (
  filterState: FilterState,
  formHasData: boolean,
  selectedAnalysis: GroupAnalysisType,
  mappingData?: MappingData
) => {
  // Send filter data for analysis type
  if (filterState && formHasData) {
    TagManager.dataLayer({
      dataLayer: {
        event: getGoogleEventForAnalysis(selectedAnalysis, GoogleEvents.filter),
      },
    });
  }

  // Send network data
  if (filterState.network && filterState.network !== 'all') {
    sendGoogleEvents(
      filterState.network,
      `${GoogleEvents.filter}_network_${NetworkType[
        Number(filterState.network)
      ].toLocaleLowerCase()}`
    );
  }

  // Send wildfire ratings if any selected
  if (filterState.wildfireRatings.length > 0)
    sendGoogleEvents(
      filterState.wildfireRatings,
      GoogleEvents.wildfireFilter,
      WildfireRiskRating
    );

  // Send wildfire priority ratings if any selected
  if (filterState.wildfirePriorityRatings.length > 0)
    sendGoogleEvents(
      filterState.wildfirePriorityRatings,
      GoogleEvents.wildfirePriorityRatingFilter,
      PriorityRatingType
    );

  // Send fuel hazard ratings if any selected
  if (filterState.fuelHazardRatings.length > 0)
    sendGoogleEvents(
      filterState.fuelHazardRatings,
      GoogleEvents.fuelHazardRatingFilter,
      Rating
    );

  // Send priority ratings if any selected
  if (filterState.priorityRatings.length > 0)
    sendGoogleEvents(
      filterState.priorityRatings,
      GoogleEvents.priorityRatingFilter,
      PriorityRatingType
    );

  // Send encroachment ratings if any selected
  if (filterState.encroachmentRatings.length > 0)
    sendGoogleEvents(
      filterState.encroachmentRatings,
      GoogleEvents.encroachmentRatingFilter,
      Rating
    );

  if (filterState.hasHazardTrees)
    sendGoogleEvents(
      filterState.hasHazardTrees,
      GoogleEvents.hazardTreesToggleFilter
    );

  if (filterState.hasStrikeTrees)
    sendGoogleEvents(
      filterState.hasStrikeTrees,
      GoogleEvents.strikeTreesToggleFilter
    );

  if (filterState.hasCriticalTrees)
    sendGoogleEvents(
      filterState.hasCriticalTrees,
      GoogleEvents.criticalTreesToggleFilter
    );

  if (filterState.tags.length > 0)
    sendGoogleEvents(
      filterState.tags,
      GoogleEvents.tagsFilter,
      'tags',
      mappingData
    );

  if (filterState.species.length > 0)
    sendGoogleEvents(
      filterState.species,
      GoogleEvents.speciesFilter,
      'species',
      mappingData
    );

  // Send encroachment ratings if any selected
  for (const t of filterState.encroachmentType) {
    sendGoogleEvents(t, `${GoogleEvents.filter}_${t}`);
  }
};

interface UseFilterLogicProps {
  initialFilterState: FilterState;
  redFlagWarnings?: RedFlagWarning[];
  onSubmit: (filterState: FilterState) => void;
  onFilterUpdated?: (filterState: FilterState) => void;
}

export function useFilterLogic({
  initialFilterState,
  redFlagWarnings = [],
  onSubmit,
  onFilterUpdated,
}: UseFilterLogicProps) {
  const intl = useClientTerminology();
  const { selectedAnalysis } = useAppSelector((s) => s.analysisMap);

  // Form state
  const { register, handleSubmit, reset, setValue, control, subscribe } =
    useForm({
      defaultValues: initialFilterState,
    });

  // Reset form when initialFilterState changes
  useEffect(() => {
    reset(initialFilterState);
  }, [initialFilterState, reset]);

  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: (d) => onFilterUpdated?.(d.values),
    });

    // Unsubscribe!
    return () => callback();
  }, [onFilterUpdated, subscribe]);

  // Data fetching
  const { data: fields, isFetching } = useFetchFieldsQuery();
  const availableFields = fields?.map((i) => i.field);

  const { data: species } = useFetchFieldSpeciesQuery(undefined, {
    skip: !availableFields || !availableFields.includes('species'),
  });
  const tags = useShowClearSpanAnalysis() ? [CLEAR_TAG] : undefined;
  const priorityRatings = getRatingOptions(PriorityRatingType);
  const wildfirePriorityRatings = getRatingOptions(WildfireRiskRating);

  const { data: worklogStatusOptions } = useFetchWorkStatusQuery(undefined, {
    skip: !availableFields || !availableFields.includes('workStatus'),
  });

  const { data: networkGroups } = useFetchNetworkGroupsQuery();
  const voltageInKv = useVoltageInKv(fields);
  const hasWildfireRisk = availableFields?.includes('wildfireRating') ?? false;
  const hasHazardTrees = availableFields?.includes('hazardTreesCount') ?? false;
  const hasStrikeTrees = availableFields?.includes('strikeTreesCount') ?? false;
  const hasCriticalTrees =
    availableFields?.includes('criticalTreesCount') ?? false;
  const hasOverhang = availableFields?.includes('hasOverhang') ?? false;
  const hasUpdatedAtYear =
    availableFields?.includes('updated_at_year') ?? false;

  const sortedSpecies = species?.slice().reverse();

  const mappingData: MappingData = {
    ...(sortedSpecies && { species: sortedSpecies }),
    ...(tags && { tags }),
  };

  const networkWatch = useWatch({
    control,
    name: 'network',
    defaultValue: initialFilterState.network,
  });

  const handleFormSubmit = (formData: FilterState) => {
    // if internal filters are cleared than set clear
    formData.isSelected = !isFilterEmpty(formData);

    GoogleEventsForFilter(
      formData,
      formData.isSelected,
      selectedAnalysis,
      mappingData
    );

    if (formData.redFlagWarnings.length > 0) {
      sendGoogleEventsForRedFlagWarningFilter(
        formData.redFlagWarnings,
        redFlagWarnings
      );
    }
    if (groupIdsInFilter(formData).length > 0) {
      for (const groupId of groupIdsInFilter(formData)) {
        const groupName = getGroupNameFromId(groupId, networkGroups);
        TagManager.dataLayer({
          dataLayer: {
            event: `${GoogleEvents.filterByGroup}_${groupName}`,
          },
        });
      }
    }

    onSubmit(formData);
  };

  const resetToInitial = useCallback(() => {
    reset(initialFilterState);
  }, [reset, initialFilterState]);

  const clearForm = () => {
    // Reset the form to our empty state
    reset(initialState);

    Object.keys(initialFilterState.groups ?? {}).forEach((groupKey) => {
      setValue(`groups.${groupKey}`, []);
    });
  };

  useEffect(() => {
    if (networkWatch === ALL_NETWORKS) {
      return;
    }
  }, [networkWatch, setValue, initialFilterState]);

  // Clear redFlagWarnings filter on unmount
  useEffect(() => {
    return () => {
      TagManager.dataLayer({
        dataLayer: {
          redFlagWarningFilters: undefined,
        },
      });
    };
  }, []);

  return {
    // Form controls
    formProps: {
      register,
      handleSubmit: handleSubmit(handleFormSubmit),
      control,
      clearForm,
      resetToInitial,
    },

    // Data for form fields
    formData: {
      sortedSpecies,
      tags,
      priorityRatings,
      wildfirePriorityRatings,
      worklogStatusOptions,
      voltageInKvOptions: voltageInKv,
      redFlagWarnings,
    },

    // Field availability
    fieldFlags: {
      hasWildfireRisk,
      hasHazardTrees,
      hasStrikeTrees,
      hasCriticalTrees,
      hasOverhang,
      hasUpdatedAtYear,
    },

    // Loading state
    isFetching,

    // Terminology
    intl,
  };
}
